import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useAlert } from "../../Hooks/useAlert";
import reportLanguage from "../../language/reportLanguage";
import { authAPI, customerAssignmentAPI, reportAPI } from "../../services/api";
import Alert from "../common/Alert";
import Spinner from "../common/Spinner";

// ✅ CRITICAL: Store window reference OUTSIDE component to persist across renders
let whatsappWindow = null;

function Report() {
  const { language } = useLanguage();
  const text = reportLanguage[language];
  const { showAlert, alertConfig } = useAlert();

  const location = useLocation();
  const isReportTabActive = location.pathname === "/report";
  const isBillTabActive = location.pathname === "/bill";

  // Get previous month name
  const getPreviousMonthName = () => {
    const currentDate = new Date();
    const previousMonth =
      currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
    return text.months[previousMonth];
  };

  const previousMonthName = getPreviousMonthName();

  const [activeTab, setActiveTab] = useState("month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingBillFor, setSendingBillFor] = useState(null); // ✅ Track which bill is being sent

  // Popup state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [popupMonthOffset, setPopupMonthOffset] = useState(-1); // -1 = previous month (default)
  const [customerEntries, setCustomerEntries] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [editValues, setEditValues] = useState({
    quantityLiters: "",
    milkRate: "",
  });
  const [savingRowIdx, setSavingRowIdx] = useState(null);

  useEffect(() => {
    loadReports();
  }, [activeTab, fromDate, toDate]);

  const loadReports = async () => {
    try {
      setLoading(true);

      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const quarter = Math.ceil(month / 3);

      let response;

      if (activeTab === "month") {
        response = await reportAPI.getCustomerBills(
          "month",
          year,
          month,
          null,
          null,
          null,
        );
      } else if (activeTab === "quarter") {
        response = await reportAPI.getCustomerBills(
          "quarter",
          year,
          null,
          quarter,
          null,
          null,
        );
      } else if (activeTab === "custom" && fromDate && toDate) {
        response = await reportAPI.getCustomerBills(
          "custom",
          null,
          null,
          null,
          fromDate,
          toDate,
        );
      }

      if (response && response.success) {
        let data = response.data;
        const currentUser = authAPI.getCurrentUser();
        if (currentUser?.role === "Employee" && currentUser?.employeeId) {
          const empResp = await customerAssignmentAPI.getMyCustomers(currentUser.employeeId);
          if (empResp.success && Array.isArray(empResp.data)) {
            const assignedIds = new Set(empResp.data.map(c => c.customerId));
            data = data.filter(r => assignedIds.has(r.customerId));
          }
        }
        setReports(
          data.map((r) => ({
            id: r.customerId,
            name: r.customerName,
            currentBillWithAllowance: r.currentBillWithAllowance,
            pending: r.pendingAmount,
            whatsappNo: r.whatsAppNo,
            totalDue: r.totalDue,
          })),
        );
      }
    } catch (error) {
      console.error("Error loading reports:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Failed to load reports. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // --- Popup Helpers ---
  const getPopupMonthYear = useCallback((offset) => {
    const d = new Date();
    d.setDate(1); // prevent day-overflow when current day > last day of target month
    d.setMonth(d.getMonth() + offset);
    return { year: d.getFullYear(), month: d.getMonth() + 1, date: d };
  }, []);

  const getMonthLabel = (offset) => {
    const { date } = getPopupMonthYear(offset);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  // Returns a Tailwind className for the popup row background based on milk quantity vs default.
  // Green  → extra milk (qty > default)
  // Red    → leave day (qty === 0)
  // Orange → less than default but still some (0 < qty < default)
  const getRowClass = (entry) => {
    const defaultVal = entry.defaultMilkLiters || 0;
    const qty = entry.quantityLiters || 0;
    if (qty === 0) {
      // Red for leave days (no milk entry)
      return "bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50";
    }
    if (qty > defaultVal && defaultVal > 0) {
      // Green when customer gave more milk than their default
      return "bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50";
    }
    if (qty < defaultVal && defaultVal > 0) {
      // Orange when customer gave less than default but still some milk
      return "bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50";
    }
    // Exactly equal to default — no special highlight
    return "hover:bg-slate-50 dark:hover:bg-slate-700/40";
  };

  const loadCustomerEntries = useCallback(
    async (customerId, offset) => {
      setPopupLoading(true);
      setCustomerEntries([]);
      setEditingRowIdx(null);
      try {
        const { year, month } = getPopupMonthYear(offset);
        const response = await reportAPI.getCustomerMonthlyEntries(
          customerId,
          year,
          month,
        );
        if (response && response.success) {
          setCustomerEntries(response.data || []);
        } else {
          setCustomerEntries([]);
        }
      } catch (error) {
        console.error("Error loading customer entries:", error);
        setCustomerEntries([]);
      } finally {
        setPopupLoading(false);
      }
    },
    [getPopupMonthYear],
  );

  const handleOpenPopup = (customer) => {
    setSelectedCustomer(customer);
    loadCustomerEntries(customer.id, popupMonthOffset);
  };

  const handleClosePopup = () => {
    setSelectedCustomer(null);
    setCustomerEntries([]);
    setEditingRowIdx(null);
  };

  const handlePopupMonthChange = (offset) => {
    setPopupMonthOffset(offset);
    loadCustomerEntries(selectedCustomer.id, offset);
  };

  const handleEditRow = (idx, entry) => {
    setEditingRowIdx(idx);
    setEditValues({
      quantityLiters: entry.quantityLiters ?? "",
      milkRate: entry.milkRate ?? "",
    });
  };

  const handleCancelEdit = () => {
    setEditingRowIdx(null);
    setEditValues({ quantityLiters: "", milkRate: "" });
  };

  const handleSaveRow = async (idx) => {
    const entry = customerEntries[idx];
    setSavingRowIdx(idx);
    try {
      const response = await reportAPI.updateCustomerDailyEntry(
        entry.entryId,
        parseFloat(editValues.quantityLiters) || 0,
        parseFloat(editValues.milkRate) || 0,
      );
      if (response && response.success) {
        const updated = [...customerEntries];
        updated[idx] = {
          ...entry,
          quantityLiters: parseFloat(editValues.quantityLiters) || 0,
          milkRate: parseFloat(editValues.milkRate) || 0,
        };
        setCustomerEntries(updated);
        setEditingRowIdx(null);
        // Reload customer list to refresh current bill and pending amounts
        loadReports();
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: response?.message || "Failed to save entry.",
        });
      }
    } catch (error) {
      await showAlert({
        type: "error",
        title: "Error",
        message: "Failed to save entry.",
      });
    } finally {
      setSavingRowIdx(null);
    }
  };

  const filteredReports = useMemo(
    () =>
      reports.filter((r) =>
        r.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [search, reports],
  );

  const handleSendReport = async (customer) => {
    if (!customer.whatsappNo) {
      await showAlert({
        type: "warning",
        title: "Missing Information",
        message: `No WhatsApp number available for ${customer.name}`,
      });
      return;
    }

    try {
      setSendingBillFor(customer.id); // ✅ Show loading for specific customer

      // Map language code to full language name for API
      const languageParam = language === "en" ? "english" : "marathi";

      // Use currently selected month offset for the bill
      const { year, month } = getPopupMonthYear(popupMonthOffset);

      const response = await reportAPI.sendMonthlyBill(
        customer.id,
        languageParam,
        year,
        month,
      );

      if (response && response.success && response.data) {
        const whatsappUrl = response.data.whatsAppUrl;

        if (whatsappUrl) {
          // ✅ Bulletproof iOS detection
          const isIOS =
            /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" &&
              navigator.maxTouchPoints > 1) ||
            (navigator.userAgent.includes("Mac") && "ontouchend" in document);

          const isAndroid = /Android/i.test(navigator.userAgent);
          const isPWA = window.navigator.standalone === true;

          let phone = "";
          let text = "";

          try {
            const sendUrl = new URL(whatsappUrl);
            if (whatsappUrl.includes("wa.me")) {
              phone = sendUrl.pathname.replace("/", "");
              text = sendUrl.searchParams.get("text") || "";
            } else {
              phone = sendUrl.searchParams.get("phone") || "";
              text = sendUrl.searchParams.get("text") || "";
            }
          } catch {}

          // Clean + add India country code
          let cleanPhone = phone.replace(/\D/g, "");
          if (cleanPhone.length === 10) {
            cleanPhone = "91" + cleanPhone;
          } else if (!cleanPhone.startsWith("91")) {
            cleanPhone = "91" + cleanPhone;
          }

          if (isIOS && isPWA) {
            // ✅ iOS PWA — whatsapp:// deep link
            window.location.href = `whatsapp://send?phone=${cleanPhone}&text=${encodeURIComponent(text)}`;
          } else if (isIOS || isAndroid) {
            // Regular mobile browser
            const a = document.createElement("a");
            a.href = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            // Desktop
            const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;
            if (!whatsappWindow || whatsappWindow.closed) {
              whatsappWindow = window.open(waUrl, "WhatsAppBilling");
            } else {
              whatsappWindow.location.href = waUrl;
              whatsappWindow.focus();
            }
          }

          console.log(`Bill sent successfully for ${customer.name}`);
        } else {
          await showAlert({
            type: "warning",
            title: "Warning",
            message: `Bill generated but WhatsApp URL not available for ${customer.name}`,
          });
        }
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: `Failed to generate bill: ${
            response?.message || "Unknown error"
          }`,
        });
      }
    } catch (error) {
      console.error("Error sending report:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send report";
      await showAlert({
        type: "error",
        title: "Error",
        message: `Error: ${errorMessage}`,
      });
    } finally {
      setSendingBillFor(null); // ✅ Clear loading state
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 bg-background-light p-4 pb-2 dark:bg-background-dark md:static md:p-6 md:pb-3">
        <div className="flex items-center justify-between md:justify-start md:hidden">
          <div className="flex items-center w-full  relative">
            {/* Logo on left */}
            <img src="/images/logo.png" alt={text.pageTitle} className="logo" />

            {/* Centered text */}
            <h1 className="absolute left-1/2 -translate-x-1/2 text-lg font-bold text-slate-900 dark:text-white">
              {text.pageTitle}
            </h1>
          </div>
        </div>

        <div className="flex gap-2 mt-0 mb-2">
          <Link
            to="/report"
            className={`flex-1 h-10 flex items-center justify-center rounded-lg text-sm ${
              isReportTabActive
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
          >
            {text.tabReport} ({previousMonthName})
          </Link>
          <Link
            to="/bill"
            className={`flex-1 h-10 flex items-center justify-center rounded-lg text-sm ${
              isBillTabActive
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
          >
            {text.tabBill} ({previousMonthName})
          </Link>
        </div>

        <div className="relative mb-2">
          <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder={text.searchCustomerPlaceholder}
            value={search}
            onChange={handleSearchChange}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <div
          className="flex gap-3 mb-1 overflow-x-auto"
          style={{ display: "none" }}
        >
          <button
            type="button"
            className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
              activeTab === "month"
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
            onClick={() => handleTabClick("month")}
          >
            {text.month}
          </button>
          <button
            type="button"
            className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
              activeTab === "quarter"
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
            onClick={() => handleTabClick("quarter")}
          >
            {text.quarter}
          </button>
          <button
            type="button"
            className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
              activeTab === "custom"
                ? "bg-primary text-white font-bold"
                : "bg-primary/20 text-primary dark:bg-primary/30"
            }`}
            onClick={() => handleTabClick("custom")}
          >
            {text.customDate || "Custom"}
          </button>
        </div>

        {activeTab === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 ">
            <label className="flex items-center gap-3">
              <span className="text-primary font-semibold w-24">
                {text.fromDate}
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                className="h-10 flex-1 rounded-lg border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-0 focus:border-primary"
              />
            </label>

            <label className="flex items-center gap-3">
              <span className="text-primary font-semibold w-24">
                {text.toDate}
              </span>
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className="h-10 flex-1 rounded-lg border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-0 focus:border-primary"
              />
            </label>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4 pt-0 pb-24 md:p-6 md:pt-0 md:pb-6">
        {loading ? (
          <Spinner />
        ) : (
          <div className="grid gap-3 md:grid-cols-1 lg:grid-cols-2">
            {filteredReports.map((report) => {
              const totalDue = report.totalDue;
              const isSending = sendingBillFor === report.id;

              return (
                <div
                  key={report.id}
                  className="customer-card bg-white dark:bg-slate-800/60 rounded-xl shadow-md p-2 md:p-3 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleOpenPopup(report)}
                >
                  <div className="font-bold text-sm text-slate-900 dark:text-white">
                    {report.name}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm mt-0">
                    <div>
                      <span className="text-slate-600 dark:text-slate-400">
                        {text.currentBill}:{" "}
                      </span>
                      <span className="font-semibold">
                        ₹{report.currentBillWithAllowance.toFixed(0)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-600 dark:text-slate-400">
                        {text.pending}:{" "}
                      </span>
                      <span
                        className={`font-semibold ${
                          report.pending > 0 ? "text-red-600" : "text-green-600"
                        }`}
                      >
                        ₹{report.pending.toFixed(0)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="md:flex items-center justify-center gap-1 px-3 py-2 bg-primary rounded-lg text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendReport(report);
                      }}
                      disabled={isSending}
                    >
                      {isSending ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></span>
                          Sending...
                        </>
                      ) : (
                        text.sendReport
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Customer Entries Popup */}
      {selectedCustomer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleClosePopup}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-primary rounded-t-2xl">
              <h2 className="text-lg font-bold text-white truncate">
                {selectedCustomer.name}
              </h2>
              <button
                onClick={handleClosePopup}
                className="text-white hover:text-slate-200 transition ml-3 flex-shrink-0"
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Month Selector Buttons */}
            <div className="flex gap-2 px-5 py-3 border-b border-slate-200 dark:border-slate-700">
              <button
                onClick={() => handlePopupMonthChange(0)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  popupMonthOffset === 0
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary dark:bg-primary/30"
                }`}
              >
                {text.currentMonth}
                <span className="block text-xs font-normal opacity-80">
                  {getMonthLabel(0)}
                </span>
              </button>
              <button
                onClick={() => handlePopupMonthChange(-1)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  popupMonthOffset === -1
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary dark:bg-primary/30"
                }`}
              >
                {text.previousMonth}
                <span className="block text-xs font-normal opacity-80">
                  {getMonthLabel(-1)}
                </span>
              </button>
              <button
                onClick={() => handlePopupMonthChange(-2)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  popupMonthOffset === -2
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary dark:bg-primary/30"
                }`}
              >
                {text.twoMonthsAgo}
                <span className="block text-xs font-normal opacity-80">
                  {getMonthLabel(-2)}
                </span>
              </button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {popupLoading ? (
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              ) : customerEntries.length === 0 ? (
                <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                  <i className="fas fa-calendar-times text-4xl mb-3 block" />
                  <p className="text-sm">{text.noEntries}</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-sm uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 text-left">{text.date}</th>
                      <th className="py-2 text-center">{text.liter}</th>
                      <th className="py-2 text-center">{text.milkType}</th>
                      <th className="py-2 text-center">{text.rate} (₹)</th>
                      <th className="py-2 text-center">{text.action}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerEntries.map((entry, idx) => {
                      const isEditing = editingRowIdx === idx;
                      const isSaving = savingRowIdx === idx;
                      const entryDate = new Date(entry.entryDate);
                      const dateStr = entryDate.toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      });
                      return (
                        <tr
                          key={idx}
                          className={`border-b border-slate-100 dark:border-slate-700 ${isEditing ? "bg-primary/5" : getRowClass(entry)}`}
                        >
                          <td className="py-2.5 text-slate-700 dark:text-slate-300 font-medium">
                            {dateStr}
                          </td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.1"
                                className="w-16 border border-primary rounded px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-700 dark:text-white"
                                value={editValues.quantityLiters}
                                onChange={(e) =>
                                  setEditValues((v) => ({
                                    ...v,
                                    quantityLiters: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="font-semibold">
                                {entry.quantityLiters?.toFixed(1)} L
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 text-center">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                entry.milkType === 0 || entry.milkType === "cow"
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                                  : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
                              }`}
                            >
                              {entry.milkType === 0 || entry.milkType === "cow"
                                ? text.cow
                                : text.buffalo}
                            </span>
                          </td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.01"
                                className="w-16 border border-primary rounded px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-700 dark:text-white"
                                value={editValues.milkRate}
                                onChange={(e) =>
                                  setEditValues((v) => ({
                                    ...v,
                                    milkRate: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span>₹{entry.milkRate?.toFixed(0)}</span>
                            )}
                          </td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleSaveRow(idx)}
                                  disabled={isSaving}
                                  className="px-2.5 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  {isSaving ? (
                                    <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  ) : (
                                    text.save
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-2.5 py-1 bg-slate-400 hover:bg-slate-500 text-white rounded text-xs font-semibold transition"
                                >
                                  {text.cancel}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditRow(idx, entry)}
                                className="text-primary hover:text-[#007aa3] transition"
                                title={text.edit}
                              >
                                <i className="fas fa-edit" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 font-bold">
                      <td className="py-2.5 text-slate-700 dark:text-slate-300 text-sm">
                        {text.totalLiters}
                      </td>
                      <td className="py-2.5 text-center text-primary text-sm">
                        {customerEntries
                          .reduce((sum, e) => sum + (e.quantityLiters || 0), 0)
                          .toFixed(1)}{" "}
                        L
                      </td>
                      <td className="py-2.5 text-center text-slate-700 dark:text-slate-300 text-sm">
                        {text.totalAmount}
                      </td>
                      <td
                        className="py-2.5 text-center text-green-700 dark:text-green-400 text-sm font-bold"
                        colSpan={2}
                      >
                        ₹
                        {customerEntries
                          .reduce(
                            (sum, e) =>
                              sum + (e.quantityLiters || 0) * (e.milkRate || 0),
                            0,
                          )
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* Popup Footer */}
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {getMonthLabel(popupMonthOffset)} — {customerEntries.length} {text.entries}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const cust = selectedCustomer;
                    handleClosePopup();
                    handleSendReport(cust);
                  }}
                  disabled={sendingBillFor === selectedCustomer?.id}
                  className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50"
                >
                  {sendingBillFor === selectedCustomer?.id ? (
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    text.sendReport
                  )}
                </button>
                <button
                  onClick={handleClosePopup}
                  className="px-5 py-2 bg-primary hover:bg-[#007aa3] text-white rounded-lg text-sm font-semibold transition"
                >
                  {text.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Dialog */}
      {alertConfig && <Alert {...alertConfig} />}
    </>
  );
}

export default Report;
